import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api/client.ts";
import { JobStatusBadge } from "../components/StatusBadge.tsx";
import { formatDate } from "../lib/format.ts";
import {
  ARTIFACT_ORDER,
  ARTIFACT_LABELS,
} from "@shared/types";
import type {
  ArtifactStatus,
  ArtifactType,
  JobArtifact,
  JobWithArtifacts,
} from "@shared/types";

// ---------------------------------------------------------------------------
// Jobs dashboard (PRD §7.8). Lists every job with its rollup status and a
// compact six-dot strip of the downstream artifact statuses.
// ---------------------------------------------------------------------------

type LoadState =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "ready"; jobs: JobWithArtifacts[] };

const DOT_STYLES: Record<ArtifactStatus, string> = {
  done: "bg-emerald-500",
  draft: "bg-amber-400",
  failed: "bg-rose-500",
  pending: "bg-slate-300",
};

const DOT_STATUS_LABEL: Record<ArtifactStatus, string> = {
  done: "done",
  draft: "draft",
  failed: "failed",
  pending: "pending",
};

function artifactByType(
  artifacts: JobArtifact[],
): Record<ArtifactType, ArtifactStatus> {
  const map = {} as Record<ArtifactType, ArtifactStatus>;
  for (const type of ARTIFACT_ORDER) {
    map[type] = artifacts.find((a) => a.type === type)?.status ?? "pending";
  }
  return map;
}

function ArtifactDots({ artifacts }: { artifacts: JobArtifact[] }) {
  const statuses = artifactByType(artifacts);
  return (
    <div className="flex items-center gap-1.5">
      {ARTIFACT_ORDER.map((type) => {
        const status = statuses[type];
        return (
          <span
            key={type}
            title={`${ARTIFACT_LABELS[type]}: ${DOT_STATUS_LABEL[status]}`}
            aria-label={`${ARTIFACT_LABELS[type]}: ${DOT_STATUS_LABEL[status]}`}
            className={`h-2.5 w-2.5 rounded-full ring-1 ring-inset ring-black/5 ${DOT_STYLES[status]}`}
          />
        );
      })}
    </div>
  );
}

function isComplete(job: JobWithArtifacts): boolean {
  return job.status === "complete";
}

function needsAttention(job: JobWithArtifacts): boolean {
  return job.status === "partial" || job.status === "failed";
}

export function Jobs() {
  const [state, setState] = useState<LoadState>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ phase: "loading" });
    api
      .listJobs()
      .then((jobs) => {
        if (!cancelled) setState({ phase: "ready", jobs });
      })
      .catch((e) => {
        if (cancelled) return;
        const message =
          e instanceof ApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : String(e);
        setState({ phase: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Jobs</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every R&amp;R job and its downstream artifacts.
          </p>
        </div>
        <Link to="/intake" className="btn-primary">
          New Job
        </Link>
      </header>

      <div className="mt-6">
        {state.phase === "loading" && <LoadingState />}
        {state.phase === "error" && <ErrorState message={state.message} />}
        {state.phase === "ready" && state.jobs.length === 0 && <EmptyState />}
        {state.phase === "ready" && state.jobs.length > 0 && (
          <JobsList jobs={state.jobs} />
        )}
      </div>
    </div>
  );
}

function JobsList({ jobs }: { jobs: JobWithArtifacts[] }) {
  const completeCount = jobs.filter(isComplete).length;
  const attentionCount = jobs.filter(needsAttention).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
        <span>
          <span className="font-semibold text-slate-900">{jobs.length}</span> total
        </span>
        <span aria-hidden="true" className="text-slate-300">
          •
        </span>
        <span>
          <span className="font-semibold text-emerald-700">{completeCount}</span> complete
        </span>
        <span aria-hidden="true" className="text-slate-300">
          •
        </span>
        <span>
          <span className="font-semibold text-amber-700">{attentionCount}</span> need
          attention
        </span>
      </div>

      {/* Desktop / wide: table */}
      <div className="card hidden overflow-hidden md:block">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Address</th>
              <th className="px-4 py-3">Removal</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Artifacts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {jobs.map((job) => (
              <tr key={job.id} className="group transition-colors hover:bg-slate-50">
                <td className="px-4 py-3">
                  <Link
                    to={`/jobs/${job.id}`}
                    className="font-medium text-slate-900 hover:text-brand-600 hover:underline"
                  >
                    {job.customer_name || "Untitled job"}
                  </Link>
                </td>
                <td className="max-w-xs truncate px-4 py-3 text-slate-600" title={job.address}>
                  {job.address || "—"}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                  {formatDate(job.removal_at)}
                </td>
                <td className="px-4 py-3">
                  <JobStatusBadge status={job.status} />
                </td>
                <td className="px-4 py-3">
                  <ArtifactDots artifacts={job.artifacts} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile / narrow: stacked cards */}
      <ul className="space-y-3 md:hidden">
        {jobs.map((job) => (
          <li key={job.id}>
            <Link
              to={`/jobs/${job.id}`}
              className="card block p-4 transition-colors hover:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-900">
                    {job.customer_name || "Untitled job"}
                  </p>
                  <p className="mt-0.5 truncate text-sm text-slate-600">
                    {job.address || "—"}
                  </p>
                </div>
                <JobStatusBadge status={job.status} />
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="text-xs text-slate-500">
                  Removal {formatDate(job.removal_at)}
                </span>
                <ArtifactDots artifacts={job.artifacts} />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="card p-10 text-center text-sm text-slate-400">Loading jobs…</div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="card p-6">
      <p className="font-semibold text-rose-700">Couldn’t load jobs</p>
      <p className="mt-2 text-sm text-slate-600">{message}</p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card flex flex-col items-center gap-3 p-12 text-center">
      <h2 className="text-lg font-semibold text-slate-900">No jobs yet</h2>
      <p className="max-w-sm text-sm text-slate-500">
        Kick off the R&amp;R automation by creating your first job from an intake.
      </p>
      <Link to="/intake" className="btn-primary mt-1">
        Create your first job
      </Link>
    </div>
  );
}
